import React, { useEffect, useRef, useReducer, ComponentProps } from "react";
import { cls } from "@wss/cls";
import useIsMounted from "~/common/useIsMounted";
import { leaveAReviewOfferText } from "./utility";
import LeaveAReviewButton from "./LeaveAReviewButton/LeaveAReviewButton";
import { ProductDetailGroupingSelect } from "~/ProductDetails/ProductDetailPageResult";
import CheckMark from "./CheckMark";
import reviewStatusReducer, { defaultStatusState } from "./reviewStatusReducer";
import fetchUserReviewStatus from "./fetchUserReviewStatus";
import { User } from "~/common/Store/models/User";
import ExtendedReview from "./ExtendedReview";
import { homepage } from "~/common/Constants/Constants";
export type UserReviewResult = {
  reviewStatus: string;
  creditsGranted: number;
};
type LeaveAReviewProps = ComponentProps<"div"> & {
  user: User;
  itemNumber: string;
  displayExtended?: boolean;
  productDetailGroupingSelect: ProductDetailGroupingSelect[];
  handleVirtualGroupingSelectChange?: (itemNumber: string) => string;
  isVirtualGroupMaster: boolean;
  isProductReviewPage?: boolean;
  image: string;
  description: string;
  productLink?: string;
  hasMediaReview?: boolean;
};
const LeaveAReview = ({
  user,
  itemNumber,
  displayExtended = false,
  productDetailGroupingSelect,
  handleVirtualGroupingSelectChange,
  isVirtualGroupMaster,
  isProductReviewPage,
  image,
  description,
  productLink = homepage,
  hasMediaReview = true,
  className = ""
}: Readonly<LeaveAReviewProps>) => {
  const mounted = useIsMounted();
  const elementToFocus = useRef<HTMLDivElement>(null);
  const [reviewStatus, statusDispatch] = useReducer(reviewStatusReducer, defaultStatusState);
  const {
    showSubmissionMessage,
    submissionHeader,
    submissionMessage,
    statusFetchComplete,
    creditsEarned,
    loggedInWithModal
  } = reviewStatus;
  const setLoggedInWithModal = (loggedInWithModal: boolean) => {
    statusDispatch({
      type: "MODAL_LOGIN",
      payload: {
        loggedInWithModal
      }
    });
  };
  useEffect(() => {
    fetchUserReviewStatus(itemNumber, user.registered, statusFetchComplete, statusDispatch);
  }, [user.registered, itemNumber, statusFetchComplete]);
  const reviewSubmissionHandler = (success: boolean) => {
    statusDispatch({
      type: "REVIEW_SUBMISSION",
      payload: {
        success
      }
    });
    elementToFocus.current?.focus();
  };
  const dataTestId = displayExtended ? "leave-review-full" : "leave-review-sidebar";
  const classes = `box padded block mb-5 ${className}
    ${displayExtended ? "full-review clearfix border-box p-8" : "mt-5"}
    ${isProductReviewPage ? "mr-0" : ""}
    ${hasMediaReview ? "" : "lt:mt-0"}`;
  const containerClasses = `flex flex-col lg:flex-row box-border w-full ${className}`;
  const leftColumnClasses = displayExtended ? "lg:w-[35%]" : "";
  const headingClasses = [displayExtended ? "text-lg" : "text-xl", "font-semibold", "w-full", "leading-tight", "mb-2"].join(" ");
  const summaryClasses = ["summary", "w-full", displayExtended ? "mb-0 lg:mb-4 text-sm" : "text-base"].join(" ");
  return mounted && (!user.registered && !loggedInWithModal || statusFetchComplete) ? <div id="writeAReview" className={classes} data-testid={dataTestId} style={{
    scrollMarginTop: "60px"
  }} aria-live="polite" tabIndex={-1} data-sentry-component="LeaveAReview" data-sentry-source-file="index.tsx">
      {showSubmissionMessage ? <div ref={elementToFocus} tabIndex={-1} className={cls("text-center", "outline-none")}>
          <div>
            <CheckMark />
            <p className="sr-only">Success</p>
          </div>
          <h2 className={cls("text-lg", "leading-[21px]", "xs:text-2xl", "xs:leading-[30px]", "m-0")}>
            {submissionHeader}
          </h2>
          {submissionMessage && <span className="text-sm leading-tight inline-block mt-2">
              {submissionMessage}
              {creditsEarned && <strong> {creditsEarned}</strong>}
            </span>}
        </div> : <div className={containerClasses}>
          <div className={leftColumnClasses}>
            <p className={headingClasses}>Leave a review of this product!</p>
            <p className={summaryClasses}>
              {leaveAReviewOfferText(displayExtended)}
            </p>
            <div className="lg:hidden">
              {displayExtended && <ExtendedReview />}
            </div>
            <LeaveAReviewButton itemNumber={itemNumber} user={user} productDetailGroupingSelect={productDetailGroupingSelect} handleVirtualGroupingSelectChange={handleVirtualGroupingSelectChange} isVirtualGroupMaster={isVirtualGroupMaster} reviewSubmissionHandler={reviewSubmissionHandler} displayExtended={displayExtended} image={image} description={description} productLink={productLink} loggedInWithModal={loggedInWithModal} setLoggedInWithModal={setLoggedInWithModal} />
          </div>
          <div className={`hidden ml-4 lg:block  ${displayExtended ? "lg:w-[65%]" : ""}`}>
            {displayExtended && <ExtendedReview />}
          </div>
        </div>}
    </div> : <></>;
};
export default LeaveAReview;